// Notification types for frontend
// Feature: 015-we-want-to
// Mirrors shared/lib/notification-types.ts

export enum NotificationType {
  NFT_RECEIVED_OFFER = 'NFT_RECEIVED_OFFER',
  NFT_BOUGHT = 'NFT_BOUGHT',
  NFT_SOLD = 'NFT_SOLD',
  NFT_OFFER_ACCEPTED = 'NFT_OFFER_ACCEPTED',
  NFT_TRANSFER_OFFER_RECEIVED = 'NFT_TRANSFER_OFFER_RECEIVED',
  COLLECTION_OFFER_CLAIM_AVAILABLE = 'COLLECTION_OFFER_CLAIM_AVAILABLE',
  FOLLOWED_WALLET_BUY = 'FOLLOWED_WALLET_BUY',
  FOLLOWED_WALLET_SELL = 'FOLLOWED_WALLET_SELL',
  COLLECTION_NEW_LISTING = 'COLLECTION_NEW_LISTING',
  COLLECTION_NEW_FLOOR = 'COLLECTION_NEW_FLOOR',
  COLLECTION_SALE = 'COLLECTION_SALE',
  COLLECTION_MINT = 'COLLECTION_MINT',
  // Launchpad notifications
  LAUNCHPAD_MINT_COMPLETED = 'LAUNCHPAD_MINT_COMPLETED',
  LAUNCHPAD_SETTLEMENT_RECEIVED = 'LAUNCHPAD_SETTLEMENT_RECEIVED',
  LAUNCHPAD_LAUNCH_COMPLETED = 'LAUNCHPAD_LAUNCH_COMPLETED',
  LAUNCHPAD_MINTER_REVOKED = 'LAUNCHPAD_MINTER_REVOKED',
  // Rewards notifications
  REWARDS_CLAIM_PERIOD_OPENED = 'REWARDS_CLAIM_PERIOD_OPENED',
  // Ambassador Program notifications
  AMBASSADOR_WELCOME = 'AMBASSADOR_WELCOME',
  AMBASSADOR_REMOVED = 'AMBASSADOR_REMOVED',
  AMBASSADOR_SELECTED = 'AMBASSADOR_SELECTED',
  AMBASSADOR_MINT_LIVE = 'AMBASSADOR_MINT_LIVE',
  AMBASSADOR_CLAIM_EXPIRING = 'AMBASSADOR_CLAIM_EXPIRING',
  AMBASSADOR_POST_REMINDER = 'AMBASSADOR_POST_REMINDER',
  AMBASSADOR_POST_MISSED = 'AMBASSADOR_POST_MISSED',
  AMBASSADOR_VIOLATION = 'AMBASSADOR_VIOLATION',
  AMBASSADOR_APPLICATION_RECEIVED = 'AMBASSADOR_APPLICATION_RECEIVED',
  AMBASSADOR_APPLICATION_APPROVED = 'AMBASSADOR_APPLICATION_APPROVED',
  AMBASSADOR_APPLICATION_REJECTED = 'AMBASSADOR_APPLICATION_REJECTED',
  // Auction notifications
  AUCTION_OUTBID = 'AUCTION_OUTBID',
  AUCTION_WON = 'AUCTION_WON',
  AUCTION_SOLD = 'AUCTION_SOLD',
  AUCTION_NO_RESERVE_MET = 'AUCTION_NO_RESERVE_MET',
}

// Marketplace can be any string returned by getMarketplaceName():
// 'xrp.deals', 'xrp.cafe', 'bidds.com', 'opmarket.ai', 'P2P', 'Other', etc.
export type Marketplace = string;

export interface BaseNotificationPayload {
  version: number;
}

export interface NFTInfo {
  nftTokenId: string;
  nftName: string | null;
  nftImageUrl: string | null;
  nftThumbnailUrl: string | null;
}

export interface CollectionInfo {
  collectionId?: string;
  collectionName?: string;
}

// NFT-specific notification payloads
export interface NFTReceivedOfferPayload extends BaseNotificationPayload, NFTInfo, CollectionInfo {
  offerId: string;
  offerAmountXrp: number;
  offeringWallet: string;
  marketplace: Marketplace;
  offerCreatedAt: string;
}

export interface NFTBoughtPayload extends BaseNotificationPayload, NFTInfo, CollectionInfo {
  salePrice: number;
  buyerWallet: string;
  sellerWallet: string;
  marketplace: Marketplace;
  transactionHash: string;
  transactionTimestamp: string;
}

export interface NFTSoldPayload extends BaseNotificationPayload, NFTInfo, CollectionInfo {
  salePrice: number;
  buyerWallet: string;
  sellerWallet: string;
  marketplace: Marketplace;
  transactionHash: string;
  transactionTimestamp: string;
}

export interface NFTOfferAcceptedPayload extends BaseNotificationPayload, NFTInfo, CollectionInfo {
  salePrice: number;
  buyerWallet: string;
  sellerWallet: string;
  marketplace: Marketplace;
  transactionHash: string;
  transactionTimestamp: string;
}

export interface NFTTransferOfferReceivedPayload extends BaseNotificationPayload, NFTInfo, CollectionInfo {
  offerId: string;
  offeringWallet: string;
  recipientWallet: string;
  offerCreatedAt: string;
}

export interface CollectionOfferClaimPayload extends BaseNotificationPayload, NFTInfo {
  offerId: string;
  offeringWallet: string;
  offerCreatedAt: string;
  collectionId: string;
  collectionName: string;
  collectionOfferId: string;
  purchasePrice: number;
  sellerWallet: string;
  filledAt: string;
  rarityRank?: number | null;
  collectionTotalSupply?: number | null;
  collectionFloorPrice?: number | null;
  traits?: Record<string, string>;
}

// Collection-specific notification payloads
export interface CollectionNewListingPayload extends BaseNotificationPayload, NFTInfo, CollectionInfo {
  listingPrice: number;
  marketplace: Marketplace;
  listedAt: string;
}

export interface CollectionNewFloorPayload extends BaseNotificationPayload, CollectionInfo {
  newFloorPrice: number;
  previousFloorPrice: number | null;
  nftTokenId?: string;
  nftName?: string | null;
  nftImageUrl?: string | null;
  nftThumbnailUrl?: string | null;
}

export interface CollectionSalePayload extends BaseNotificationPayload, NFTInfo, CollectionInfo {
  salePrice: number;
  marketplace: Marketplace;
  soldAt: string;
}

export type NotificationPayload =
  | NFTReceivedOfferPayload
  | NFTBoughtPayload
  | NFTSoldPayload
  | NFTOfferAcceptedPayload
  | NFTTransferOfferReceivedPayload
  | CollectionOfferClaimPayload
  | CollectionNewListingPayload
  | CollectionNewFloorPayload
  | CollectionSalePayload;

export interface UserNotification {
  id: string;
  user_id: string;
  type: NotificationType;
  data: NotificationPayload;
  created_at: string;
  read_at: string | null;
  email_sent_at: string | null;
}

// Alert configuration types
export interface TraitFilters {
  [traitType: string]: string[];
}

export interface AlertConfig {
  id: string;
  user_id: string;
  collection_id: string;
  notification_type: NotificationType.COLLECTION_NEW_LISTING | NotificationType.COLLECTION_NEW_FLOOR | NotificationType.COLLECTION_SALE;
  min_amount_in_xrp: number | null;
  max_amount_in_xrp: number | null;
  trait_filters: TraitFilters | null;
  created_at: string;
}
